import classNames from "classnames"
import React from "react"

import { Color } from "../../../constants/V2/color"
import GatsbyStoryblokImage from "../../elements/V2/GatsbyStoryblokImage"
import Section from "../../elements/V2/Section"
import Typography from "../../elements/V2/Typography"
import LayoutItem from "../Layouts/LayoutItem"

import Callout from "@components/elements/V2/Callout"
import { getUrlFromStoryblokLink } from "@utils/storyblok"
import {
  getAccentColor,
  getBackgroundColorClass,
  getShadowColorClass,
} from "@utils/V2/color"

type ScrollingLogoProps = {
  title?: string
  imageUrl: string
  backgroundColor: Color
  rounded?: boolean
  callout?: Storyblok.Callout
  mobileGridOrder?: number
  desktopOrientation?: "horizontal" | "vertical"
}

const ScrollingLogo = ({
  title,
  imageUrl,
  backgroundColor,
  rounded = true,
  callout,
  mobileGridOrder,
  desktopOrientation = "vertical",
}: ScrollingLogoProps) => {
  return (
    <LayoutItem
      as={Section}
      className={classNames(
        getAccentColor(backgroundColor),
        getBackgroundColorClass(backgroundColor),
        "pb-lg -mb-lg relative flex flex-col gap-40 pt-16 md:pt-20 lg:pt-32",
        {
          "rounded-t-xl -mt-xl": rounded,
          "sm:grid sm:grid-cols-2": desktopOrientation === "horizontal",
        }
      )}
      data-testid="scrolling-logo-component"
      mobileGridOrder={mobileGridOrder}
    >
      <div
        className={classNames("flex flex-row items-center gap-16 pb-10", {
          "md:pb-12": desktopOrientation === "vertical",
          "sm:pb-0": desktopOrientation === "horizontal",
        })}
      >
        {callout && (
          <Callout
            color={getAccentColor(backgroundColor)}
            text={callout.text}
            textSize={
              desktopOrientation === "horizontal" ? "body-lg" : "body-md"
            }
            highlightedWord={callout.highlightedWord}
            highlightColor={callout.highlightColor}
            hasBorder={callout.hasBorder}
            hasIcon={callout.hasIcon}
            ctaText={callout.ctaText}
            ctaLink={
              callout.ctaLink && getUrlFromStoryblokLink(callout.ctaLink)
            }
          />
        )}
        {title && (
          <Typography
            text={title}
            weight="book"
            font="grotesk"
            size="body-lg"
            color={getAccentColor(backgroundColor)}
          />
        )}
      </div>
      <div className="relative flex items-center overflow-hidden">
        <div className="flex animate-[scroll_100s_linear_infinite]">
          {/* Mapping the img to avoid the white space before the marquee completes and the rerender jump */}
          {Array.from({ length: 4 }).map((_, index) => (
            <GatsbyStoryblokImage
              image={imageUrl}
              alt="Marquee logo"
              className="max-w-none"
              key={`scrolling-logo-${index}`}
              width={1336}
              height={46}
            />
          ))}
        </div>
        <div
          className={classNames(
            "absolute left-0 h-full shadow-[0_0_5px_5px]",
            getShadowColorClass(backgroundColor)
          )}
        />
        <div
          className={classNames(
            "absolute right-0 h-full shadow-[0_0_5px_5px]",
            getShadowColorClass(backgroundColor)
          )}
        />
      </div>
    </LayoutItem>
  )
}

export default ScrollingLogo
